import React, { useState, useEffect } from 'react';
import { Container, Button, Card, ProgressBar, OverlayTrigger, Tooltip, Modal } from 'react-bootstrap';
import { collection, getDocs, doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../../../firebase';
import Icon from '../../UI/Icon';
import PhoneUnavailableMessage from '../../UI/PhoneUnavailableMessage';
import { getImageByGermanWord } from '../../../services/gameService';
import { getProblemSounds } from '../../../services/pronunciationTestService';
import gameWon from '../../../assets/gameWon.png';
import gameLost from '../../../assets/gameLost.png';
import sprechheldenHeldFlying from '../../../assets/sprechheldenHeld_flying.png';
import helpLogo from '../../../assets/logo_logo_sh.png';
import background1 from '../../../assets/background_1.png';
import Confetti from 'react-confetti';
import { LuGamepad2 } from "react-icons/lu";
import { isPhone as checkIsPhone, isTabletPortrait, setupDeviceDetection } from '../../../utils/deviceDetection';

// Add CSS animations and responsive styles
const styles = `
  @keyframes fadeInBounce {
    0% {
      opacity: 0;
      transform: scale(0.3);
    }
    50% {
      opacity: 1;
      transform: scale(1.05);
    }
    70% {
      transform: scale(0.9);
    }
    100% {
      opacity: 1;
      transform: scale(1);
    }
  }
  
  @keyframes slideInFromTop {
    0% {
      opacity: 0;
      transform: translateY(-20px);
    }
    100% {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  @keyframes fadeInOverlay {
    0% {
      opacity: 0;
      backdrop-filter: blur(0px);
    }
    100% {
      opacity: 1;
      backdrop-filter: blur(1px);
    }
  }
  
  @keyframes bounceCheckmark {
    0% {
      transform: scale(0) rotate(0deg);
      opacity: 0;
    }
    50% {
      transform: scale(1.3) rotate(180deg);
    }
    100% {
      transform: scale(1) rotate(360deg);
      opacity: 1;
    }
  }
  
  @keyframes minimalPairFadeIn {
    0% {
      opacity: 0;
    }
    100% {
      opacity: 1;
    }
  }
  
  @keyframes float {
    0% {
      transform: translateY(0px);
    }
    50% {
      transform: translateY(-15px);
    }
    100% {
      transform: translateY(0px);
    }
  }
  
  @keyframes glow {
    0% {
      box-shadow: 0 0 5px rgba(255, 215, 0, 0.5);
    }
    50% {
      box-shadow: 0 0 20px rgba(255, 215, 0, 0.8), 0 0 30px rgba(255, 215, 0, 0.6);
    }
    100% {
      box-shadow: 0 0 5px rgba(255, 215, 0, 0.5);
    }
  }

  .game-wrapper {
    max-width: 960px;
    margin: 0 auto;
    padding: 20px;
    min-height: clamp(480px, 60vh, 800px);
  }

  .game-content {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: clamp(40px, 6vw, 100px);
    min-height: 400px;
    position: relative;
  }

  .game-card {
    width: 300px;
    height: 300px;
    border-radius: 20px;
    transition: all 0.3s ease;
    border: 2px solid #dee2e6;
  }

  .game-card:hover {
    transform: scale(1.05) translateY(-5px);
    border-color: #88D8B0;
  }

  .game-card.disabled {
    cursor: default;
  }

  .game-card.disabled:hover {
    transform: none;
    border-color: #dee2e6;
  }

  .game-card.correct {
    background-color: #d4edda;
  }

  .game-card.incorrect {
    background-color: #f8d7da;
  }
  
  .game-card.glowing {
    animation: glow 1s ease-in-out infinite;
  }

  .control-button {
    minWidth: 60px;
    minHeight: 60px;
    borderRadius: 50%;
    border: 2px solid #dee2e6;
    transition: all 0.3s ease;
  }
  
  .control-button:hover {
    transform: translateY(-2px);
    borderColor: #88D8B0;
  }

  .header-section {
    height: 80px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    text-align: center;
  }

  .progress-section {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 20px 20px;
  }

  .final-score-container {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 500px;
  }

  .final-score-card {
    padding: 3rem;
    border-radius: 20px;
    background-color: #ffffff;
    border: 2px solid #dee2e6;
    animation: slideInFromTop 0.8s ease-out;
  }

  .loading-container {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 60vh;
  }

  /* iPad Portrait - Optimize card sizes and spacing */
  @media (min-width: 768px) and (max-width: 1024px) and (orientation: portrait) {
    .game-wrapper {
      padding: 15px;
      max-width: 100%;
    }

    .game-card {
      width: 240px;
      height: 240px;
    }

    .game-content {
      gap: clamp(30px, 4vw, 50px);
      min-height: 320px;
    }

    .control-button {
      minWidth: 50px;
      minHeight: 50px;
    }

    .game-complete-speech-bubble {
      left: 280px !important;
      min-width: 240px !important;
      font-size: 16px !important;
    }

    .final-score-container {
      min-height: 400px;
    }
  }
`;

// Inject styles into document head
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.innerText = styles;
  document.head.appendChild(styleSheet);
}

function MinimalPairGame({ onBackToGames, difficulty }) {
  const [wordPairs, setWordPairs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPairIndex, setCurrentPairIndex] = useState(0);
  const [score, setScore] = useState(0);
  const [gameCompleted, setGameCompleted] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [confettiEnabled, setConfettiEnabled] = useState(false);
  const [userGuess, setUserGuess] = useState(null);
  const [hasGuessed, setHasGuessed] = useState(false);
  const [wordImages, setWordImages] = useState({});
  
  // Sound selection state
  const [availableLauts, setAvailableLauts] = useState([]);
  const [selectedLauts, setSelectedLauts] = useState([]);
  const [showLautModal, setShowLautModal] = useState(true);
  const [allMinimalPairs, setAllMinimalPairs] = useState([]);
  const [isPhone, setIsPhone] = useState(false);
  const [isTabletPortraitMode, setIsTabletPortraitMode] = useState(false);
  const [glowingCard, setGlowingCard] = useState(null);
  const [pairOffset, setPairOffset] = useState(0); // Track which set of pairs we're showing
  const [shuffledFilteredPairs, setShuffledFilteredPairs] = useState([]); // Store shuffled pairs
  const [shuffleTrigger, setShuffleTrigger] = useState(0); // Trigger re-shuffle

  // Shuffle array using Fisher-Yates algorithm
  const shuffleArray = (array) => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  // Phone and tablet portrait detection on component mount
  useEffect(() => {
    const cleanup = setupDeviceDetection(() => {
      setIsPhone(checkIsPhone());
      setIsTabletPortraitMode(isTabletPortrait());
    });
    return cleanup;
  }, []);

  // Fetch minimal_pairs collection from Firebase
  useEffect(() => {
    const fetchMinimalPairs = async () => {
      try {
        const minimalPairsRef = collection(db, 'minimal_pairs');
        const snapshot = await getDocs(minimalPairsRef);

        if (!snapshot.empty) {

          // Collect all unique word references first
          const wordRefsSet = new Set();
          const pairsWithRefs = [];

          snapshot.docs.forEach(docSnap => {
            const data = docSnap.data();
            if (data.word1Ref && data.word2Ref) {
              wordRefsSet.add(data.word1Ref);
              wordRefsSet.add(data.word2Ref);
              pairsWithRefs.push({
                word1Ref: data.word1Ref,
                word2Ref: data.word2Ref
              });
            }
          });

          // Fetch ALL word documents in parallel (much faster!)
          const wordRefs = Array.from(wordRefsSet);

          const wordDocsPromises = wordRefs.map(wordRef =>
            getDoc(doc(db, 'Words', wordRef))
          );

          const wordDocs = await Promise.all(wordDocsPromises);

          // Create a map of word references to word data
          const wordDataMap = {};
          wordDocs.forEach((wordDoc, index) => {
            if (wordDoc.exists()) {
              wordDataMap[wordRefs[index]] = wordDoc.data();
            }
          });

          // Build minimal pairs using the cached word data
          const minimalPairsData = [];

          pairsWithRefs.forEach(pair => {
            const word1Data = wordDataMap[pair.word1Ref];
            const word2Data = wordDataMap[pair.word2Ref];

            if (word1Data && word2Data) {
              minimalPairsData.push([
                { word: word1Data.word, audio: word1Data.audio, laut: word1Data.laut || [] },
                { word: word2Data.word, audio: word2Data.audio, laut: word2Data.laut || [] }
              ]);
            }
          });

          setAllMinimalPairs(minimalPairsData);

          // Extract unique lauts from minimal pair words only
          const allLautsFromMinimalPairs = [];

          // Iterate through wordDataMap to collect all lauts
          Object.values(wordDataMap).forEach(wordData => {
            if (wordData.laut && Array.isArray(wordData.laut)) {
              allLautsFromMinimalPairs.push(...wordData.laut);
            }
          });

          // Process lauts: trim, filter, deduplicate, and sort
          const uniqueLauts = allLautsFromMinimalPairs
            .map(laut => laut && typeof laut === 'string' ? laut.trim() : laut)
            .filter(laut => laut && typeof laut === 'string' && laut !== '')
            .filter((laut, index, array) => array.indexOf(laut) === index)
            .sort();

          setAvailableLauts(uniqueLauts);

          // Pre-select problem sounds
          const user = auth.currentUser;
          if (user) {
            const problemSoundsData = await getProblemSounds(user.uid);
            const severeSounds = problemSoundsData.severeProblemSounds;

            if (severeSounds && Array.isArray(severeSounds) && severeSounds.length > 0) {
              const soundsToPreselect = severeSounds
                .filter(sound => uniqueLauts.some(laut => laut.toLowerCase() === sound.toLowerCase()))
                .map(sound => uniqueLauts.find(laut => laut.toLowerCase() === sound.toLowerCase()))
                .filter(Boolean)
                .slice(0, 2);

              setSelectedLauts(soundsToPreselect);
            }
          }

        } else {
        }
      } catch (error) {
        console.error('Error fetching minimal_pairs collection:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchMinimalPairs();
  }, []);

  // Filter and shuffle minimal pairs based on selected sounds
  useEffect(() => {
    if (selectedLauts.length === 0 || allMinimalPairs.length === 0) {
      setShuffledFilteredPairs([]);
      setWordPairs([]);
      return;
    }

    // Filter minimal pairs where at least one word in the pair has the selected sounds
    const filteredPairs = allMinimalPairs.filter(pair => {
      const word1HasLaut = Array.isArray(pair[0].laut) && selectedLauts.some(selectedLaut =>
        pair[0].laut.includes(selectedLaut)
      );
      const word2HasLaut = Array.isArray(pair[1].laut) && selectedLauts.some(selectedLaut =>
        pair[1].laut.includes(selectedLaut)
      );
      return word1HasLaut || word2HasLaut;
    });

    // Shuffle the filtered pairs for randomness
    const shuffled = shuffleArray(filteredPairs);
    setShuffledFilteredPairs(shuffled);
  }, [selectedLauts, allMinimalPairs, shuffleTrigger]);

  // Update word pairs when shuffled pairs or offset changes
  useEffect(() => {
    if (shuffledFilteredPairs.length === 0) {
      setWordPairs([]);
      return;
    }

    // Always get exactly 5 pairs, wrapping around if necessary
    let gamePairs = [];
    const totalPairs = shuffledFilteredPairs.length;

    if (pairOffset + 5 <= totalPairs) {
      // Enough pairs remaining, no wrap needed
      gamePairs = shuffledFilteredPairs.slice(pairOffset, pairOffset + 5);
    } else {
      // Not enough pairs, wrap around to the beginning
      const remainingPairs = shuffledFilteredPairs.slice(pairOffset);
      const neededFromStart = 5 - remainingPairs.length;
      const pairsFromStart = shuffledFilteredPairs.slice(0, neededFromStart);
      gamePairs = [...remainingPairs, ...pairsFromStart];
    }

    setWordPairs(gamePairs);

    // Fetch images for all words in the filtered game
    if (gamePairs.length > 0) {
      const fetchImages = async () => {
        const allWords = gamePairs.flat().map(wordData => wordData.word);
        const imagePromises = allWords.map(async (word) => {
          const imageData = await getImageByGermanWord(word);
          return { word, imageData };
        });

        const imageResults = await Promise.all(imagePromises);
        const imageMap = {};
        imageResults.forEach(({ word, imageData }) => {
          if (imageData) {
            imageMap[word] = imageData;
          }
        });

        setWordImages(imageMap);
      };

      fetchImages();
    }
  }, [shuffledFilteredPairs, pairOffset]);

  // Handle sound selection with max 2 validation
  const handleLautSelection = (laut) => {
    setSelectedLauts(prev => {
      if (prev.includes(laut)) {
        // Remove laut if already selected
        return prev.filter(l => l !== laut);
      } else {
        // Add laut if not selected and under limit
        if (prev.length < 2) {
          return [...prev, laut];
        }
        return prev; // Don't add if already at max
      }
    });
  };

  // Start game with selected lauts
  const startGameWithLauts = () => {
    if (selectedLauts.length === 0) {
      alert('Bitte wählen Sie mindestens einen Laut aus!');
      return;
    }
    // Reset game progress when new sounds are selected
    resetGame();
    // Reset to first set of pairs and trigger reshuffle for new sounds
    setPairOffset(0);
    setShuffleTrigger(prev => prev + 1);
    setShowLautModal(false);
  };

  // Reset laut selection
  const resetLautSelection = () => {
    setSelectedLauts([]);
  };

  // Get current pair
  const currentPair = wordPairs[currentPairIndex];
  // Randomly select which word to play
  const [targetIdx, setTargetIdx] = useState(() => Math.floor(Math.random() * 2));

  // Auto-play audio when a new question is loaded
  useEffect(() => {
    if (currentPair && !gameCompleted && !hasGuessed && !showLautModal) {
      // Add a small delay to ensure the UI has updated
      const timer = setTimeout(() => {
        speak();
      }, 500);

      return () => clearTimeout(timer);
    }
  }, [currentPairIndex, currentPair, showLautModal]); // Trigger when question changes or modal closes

  const speak = () => {
    if (!currentPair || !currentPair[targetIdx]?.audio) {
      console.error('No audio available for this word');
      return;
    }
    
    const audio = new Audio(currentPair[targetIdx].audio);
    audio.play().catch(error => {
      console.error('Error playing audio:', error);
    });
  };

  // Handle user guess and provide feedback
  const handleGuess = (idx) => {
    setUserGuess(idx);
    setHasGuessed(true);
    
    // Check if correct
    const isCorrect = idx === targetIdx;
    const newScore = isCorrect ? score + 1 : score;
    
    if (isCorrect) {
      setScore(newScore);
    }
    
    // Move to next pair after a short delay
    setTimeout(() => {
      if (currentPairIndex < wordPairs.length - 1) {
        setCurrentPairIndex(currentPairIndex + 1);
        setUserGuess(null);
        setHasGuessed(false);
        setTargetIdx(Math.floor(Math.random() * 2));
        setGlowingCard(null);
      } else {
        // Game completed - check if perfect score for confetti
        setGameCompleted(true);
        
        // Show confetti only if user got ALL pairs correct
        const finalScore = newScore;
        const totalPairs = wordPairs.length;
        
        if (finalScore === totalPairs) {
          setConfettiEnabled(true);
          setShowConfetti(true);
          // Stop confetti after 5 seconds
          setTimeout(() => {
            setShowConfetti(false);
          }, 5000);
        }
      }
    }, 2000); // 2 second delay to show feedback
  };

  // Reset game for new round - replays current 5 pairs
  const resetGame = () => {
    setCurrentPairIndex(0);
    setScore(0);
    setGameCompleted(false);
    setShowConfetti(false);
    setConfettiEnabled(false);
    setUserGuess(null);
    setHasGuessed(false);
    setTargetIdx(Math.floor(Math.random() * 2));
    setGlowingCard(null);
    // Keep pairOffset and don't trigger re-shuffle to replay same pairs
  };

  // Continue playing with same laut but with random 5 pairs
  const continueWithSameLaut = () => {
    // Reset offset to 0 and trigger reshuffle to get new random 5 pairs
    setPairOffset(0);
    setShuffleTrigger(prev => prev + 1);

    // Reset game state
    setCurrentPairIndex(0);
    setScore(0);
    setGameCompleted(false);
    setShowConfetti(false);
    setConfettiEnabled(false);
    setUserGuess(null);
    setHasGuessed(false);
    setTargetIdx(Math.floor(Math.random() * 2));
    setGlowingCard(null);
  };

  // Help function - makes the correct answer glow for 3 seconds
  const showHelp = () => {
    if (gameCompleted || hasGuessed || !currentPair) return;
    
    // Make the correct card glow
    setGlowingCard(targetIdx);
    
    // Remove glow after 3 seconds
    setTimeout(() => {
      setGlowingCard(null);
    }, 3000);
  };

  // Render sound selection modal
  const renderLautSelectionModal = () => {
    // Debug logging when modal is opened
    if (showLautModal) {
    }
    
    return (
      <Modal show={showLautModal} onHide={onBackToGames || (() => setShowLautModal(false))} backdrop="static" keyboard={false} centered size="lg">
      <Modal.Header closeButton={true}>
        <Modal.Title>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <h4 style={{ margin: 0 }}>Laute auswählen</h4>
            <OverlayTrigger
              placement="right"
              overlay={<Tooltip id="laut-info-tooltip">Wählen Sie bis zu 2 Laute aus, um Minimalpaare mit diesen Lauten zu üben.</Tooltip>}
            >
              <div style={{ 
                width: '20px', 
                height: '20px', 
                backgroundColor: '#17a2b8', 
                borderRadius: '50%', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                cursor: 'pointer',
                color: 'white',
                fontSize: '12px',
                fontWeight: 'bold'
              }}>
                <Icon name="info" size="sm" />
              </div>
            </OverlayTrigger>
          </div>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p style={{ marginBottom: '20px', color: '#666' }}>
          Wählen Sie bis zu 2 Laute aus ({selectedLauts.length}/2 ausgewählt):
        </p>
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))', 
          gap: '15px',
          maxHeight: '400px',
          overflowY: 'auto',
          padding: '10px'
        }}>
          {availableLauts.map(laut => {
            const isSelected = selectedLauts.includes(laut);
            const isDisabled = !isSelected && selectedLauts.length >= 2;
            
            return (
              <button
                key={laut}
                onClick={() => handleLautSelection(laut)}
                disabled={isDisabled}
                style={{
                  padding: '15px 10px',
                  border: isSelected ? '3px solid #28a745' : '2px solid #dee2e6',
                  borderRadius: '10px',
                  backgroundColor: isSelected ? '#d4edda' : isDisabled ? '#f8f9fa' : '#ffffff',
                  cursor: isDisabled ? 'not-allowed' : 'pointer',
                  fontSize: '18px',
                  fontWeight: 'bold',
                  color: isSelected ? '#155724' : isDisabled ? '#6c757d' : '#333',
                  transition: 'all 0.3s ease',
                  minHeight: '60px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  opacity: isDisabled ? 0.5 : 1
                }}
                onMouseEnter={(e) => {
                  if (!isDisabled) {
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isDisabled) {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = 'none';
                  }
                }}
              >
                {laut}
              </button>
            );
          })}
        </div>
        {selectedLauts.length >= 2 && (
          <p style={{ marginTop: '15px', color: '#dc3545', fontSize: '14px' }}>
            Maximale Anzahl von Lauten erreicht. Entfernen Sie einen Laut, um einen anderen auszuwählen.
          </p>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button 
          variant="outline-secondary" 
          onClick={resetLautSelection}
          disabled={selectedLauts.length === 0}
        >
          Zurücksetzen
        </Button>
        <Button 
          variant="success" 
          onClick={startGameWithLauts}
          disabled={selectedLauts.length === 0}
          style={{
            minWidth: '150px',
            backgroundColor: '#28a745',
            borderColor: '#28a745'
          }}
        >
          Spiel starten ({selectedLauts.length} Laut{selectedLauts.length !== 1 ? 'e' : ''})
        </Button>
      </Modal.Footer>
    </Modal>
    );
  };

  // Show phone warning if on phone
  if (isPhone) {
    return <PhoneUnavailableMessage gameTitle="Laut-Detektive" onBackToGames={onBackToGames} />;
  }

  // Show loading screen
  if (loading) {
    return (
      <div className="minimal-pair-game-main-content" style={{
        position: 'relative',
        minHeight: '100vh',
        width: '100%',
        margin: 0,
        padding: isPhone ? '30px 15px' : '20px',
        overflow: 'visible'
      }}>
        {!isPhone && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            minHeight: '100vh',
            backgroundImage: `url(${background1})`,
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'cover',
            backgroundPosition: 'center center',
            opacity: 0.2,
            zIndex: 0
          }} />
        )}
      <Container style={{ position: 'relative', zIndex: 1, maxWidth: '100%' }}>
        <div className="text-center mb-4">
          <h2>Laut-Detektive</h2>
        </div>
        <div className="game-wrapper">
          <div className="loading-container">
            <div className="text-center">
              <div className="spinner-border text-primary mb-2" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p>Lade Minimalpaare...</p>
            </div>
          </div>
        </div>
      </Container>
      </div>
    );
  }

  // Show sound selection modal if no sounds selected
  if (showLautModal) {
    return (
      <div className="minimal-pair-game-main-content" style={{
        position: 'relative',
        minHeight: '100vh',
        width: '100%',
        margin: 0,
        padding: isPhone ? '30px 15px' : '20px',
        overflow: 'visible'
      }}>
        {!isPhone && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            minHeight: '100vh',
            backgroundImage: `url(${background1})`,
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'cover',
            backgroundPosition: 'center center',
            opacity: 0.2,
            zIndex: 0
          }} />
        )}
      <Container style={{ position: 'relative', zIndex: 1, maxWidth: '100%' }}>
        {renderLautSelectionModal()}
        <div className="text-center mb-4">
          <h2>Laut-Detektive</h2>
        </div>
        <div className="game-wrapper">
          <div className="loading-container">
            <div className="text-center">
              <div className="spinner-border text-primary mb-2" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p>Lade verfügbare Laute...</p>
            </div>
          </div>
        </div>
      </Container>
      </div>
    );
  }

  // Show error if no pairs loaded
  if (!wordPairs.length) {
    return (
      <div className="minimal-pair-game-main-content" style={{
        position: 'relative',
        minHeight: '100vh',
        width: '100%',
        margin: 0,
        padding: isPhone ? '30px 15px' : '20px',
        overflow: 'visible'
      }}>
        {!isPhone && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            minHeight: '100vh',
            backgroundImage: `url(${background1})`,
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'cover',
            backgroundPosition: 'center center',
            opacity: 0.2,
            zIndex: 0
          }} />
        )}
      <Container style={{ position: 'relative', zIndex: 1, maxWidth: '100%' }}>
        {renderLautSelectionModal()}
        <div className="text-center mb-4">
          <h2>Laut-Detektive</h2>
        </div>
        <div className="game-wrapper">
          <div className="loading-container">
            <div className="text-center">
              <h3>Keine Minimalpaare für die ausgewählten Laute verfügbar</h3>
              <p>Bitte wählen Sie andere Laute aus.</p>
              <Button 
                variant="primary" 
                onClick={() => setShowLautModal(true)}
                style={{ marginTop: '20px' }}
              >
                Laute ändern
              </Button>
            </div>
          </div>
        </div>
      </Container>
      </div>
    );
  }

  // Show final score screen with GameWon character
  if (gameCompleted) {
    return (
      <div className="minimal-pair-game-main-content" style={{
        position: 'relative',
        minHeight: '100vh',
        width: '100%',
        margin: 0,
        padding: isPhone ? '30px 15px' : '20px',
        overflow: 'visible'
      }}>
        {!isPhone && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            minHeight: '100vh',
            backgroundImage: `url(${background1})`,
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'cover',
            backgroundPosition: 'center center',
            opacity: 0.2,
            zIndex: 0
          }} />
        )}
      <Container style={{ position: 'relative', zIndex: 1, maxWidth: '100%' }}>
        {renderLautSelectionModal()}
        <div style={{
          backgroundColor: 'white',
          padding: '20px',
          borderRadius: '10px',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
          margin: '0 auto 30px',
          width: 'fit-content'
        }}>
          <div className="text-center">
            <h2 style={{
              margin: 0,
              color: 'inherit'
            }}>
              Laut-Detektive
            </h2>
          </div>
        </div>
        
        {/* Control Panel */}
        <div style={{
          backgroundColor: 'white',
          padding: '10px 20px',
          borderRadius: '10px',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
          margin: '0 auto',
          width: 'fit-content'
        }}>
          <div className="d-flex justify-content-center gap-3 flex-wrap">
            {onBackToGames && (
              <OverlayTrigger
                placement="bottom"
                overlay={<Tooltip id="back-to-games-tooltip">Zurück zu Spielen</Tooltip>}
              >
                <Button
                  onClick={onBackToGames}
                  variant="secondary"
                  size="lg"
                  style={{
                    minWidth: '60px',
                    minHeight: '60px',
                    borderRadius: '50%',
                    boxShadow: '0 3px 8px rgba(0, 0, 0, 0.2)'
                  }}
                >
                  <LuGamepad2 size={24} />
                </Button>
              </OverlayTrigger>
            )}
            <OverlayTrigger
              placement="bottom"
              overlay={<Tooltip id="lauts-tooltip">Ausgewählte Laute</Tooltip>}
            >
              <div
                style={{
                  minWidth: '80px',
                  height: '60px',
                  borderRadius: '30px',
                  backgroundColor: '#f8f9fa',
                  border: '2px solid #dee2e6',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '12px',
                  fontWeight: 'bold',
                  cursor: 'default',
                  boxShadow: '0 3px 8px rgba(0, 0, 0, 0.2)',
                  padding: '0 15px'
                }}
              >
                {selectedLauts.join(', ')}
              </div>
            </OverlayTrigger>
            <OverlayTrigger
              placement="bottom"
              overlay={<Tooltip id="help-tooltip">Hilfe</Tooltip>}
            >
              <Button
                onClick={showHelp}
                variant="info"
                size="lg"
                disabled={gameCompleted || hasGuessed}
                style={{
                  minWidth: '60px',
                  minHeight: '60px',
                  borderRadius: '50%',
                  boxShadow: '0 3px 8px rgba(0, 0, 0, 0.2)',
                  padding: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <img 
                  src={helpLogo} 
                  alt="Hilfe" 
                  style={{ 
                    width: '40px', 
                    height: '40px',
                    objectFit: 'contain'
                  }} 
                />
              </Button>
            </OverlayTrigger>
            <OverlayTrigger
              placement="bottom"
              overlay={<Tooltip id="new-game-tooltip">Diese Paare nochmal spielen</Tooltip>}
            >
              <Button
                onClick={resetGame}
                variant="success"
                size="lg"
                style={{
                  minWidth: '60px',
                  minHeight: '60px',
                  borderRadius: '50%',
                  boxShadow: '0 3px 8px rgba(0, 0, 0, 0.2)'
                }}
              >
                <Icon name="refresh" size="lg" />
              </Button>
            </OverlayTrigger>
            {/* Show "Keep Playing" button to play random 5 pairs */}
            <OverlayTrigger
              placement="bottom"
              overlay={<Tooltip id="continue-tooltip">Weiterspielen mit gleichem Laut</Tooltip>}
            >
              <Button
                onClick={continueWithSameLaut}
                variant="primary"
                size="lg"
                style={{
                  minWidth: '60px',
                  minHeight: '60px',
                  borderRadius: '50%',
                  boxShadow: '0 3px 8px rgba(0, 0, 0, 0.2)'
                }}
              >
                <Icon name="play" size="lg" />
              </Button>
            </OverlayTrigger>
          </div>
        </div>

        <div className="game-wrapper">
          <div className="header-section">
            <h2 style={{ margin: 0, color: 'green' }}>
              Spiel beendet!
            </h2>
          </div>

          <div className="progress-section">
            <ProgressBar 
              now={100} 
              variant="success"
              animated={false}
              style={{ height: '25px' }}
              label={`${wordPairs.length} / ${wordPairs.length}`}
            />
          </div>

          {/* Game Complete Section with GameWon/GameLost Character */}
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%',
            minHeight: '400px',
            position: 'relative',
            marginTop: '20px'
          }}>
            {/* GameWon/GameLost character with relative positioning */}
            <div style={{
              position: 'relative',
              display: 'inline-block',
              marginTop: isTabletPortraitMode ? '60px' : '0'
            }}>
              <img
                src={score === wordPairs.length ? gameWon : gameLost}
                alt={score === wordPairs.length ? "Game Won Character" : "Game Lost Character"}
                style={{
                  width: '300px',
                  height: 'auto',
                  zIndex: 1,
                  animation: 'float 3s ease-in-out infinite'
                }}
              />

              {/* Speech bubble text positioned relative to character */}
              <div
                className="game-complete-speech-bubble"
                style={{
                  position: 'absolute',
                  top: isTabletPortraitMode ? '-60px' : '-20px',
                  left: '350px',
                  transform: 'translateX(-50%)',
                  zIndex: 2,
                  backgroundColor: '#ffffff',
                  padding: '12px 20px',
                  borderRadius: '20px',
                  border: '2px solid #333333',
                  fontSize: '18px',
                  color: '#333',
                  textAlign: 'center',
                  minWidth: '280px',
                  fontFamily: '"Poppins", sans-serif',
                  fontWeight: '600'
                }}
              >
                <span>
                  {score === wordPairs.length
                    ? 'Super, du hast all deine Geräusche herausgehört'
                    : 'Versuch es nochmal!'
                  }
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Confetti Effect */}
        {confettiEnabled && (
          <Confetti
            width={window.innerWidth}
            height={window.innerHeight}
            recycle={showConfetti}
            numberOfPieces={200}
            gravity={0.3}
            style={{ position: 'fixed', top: 0, left: 0, zIndex: 9999 }}
          />
        )}
      </Container>
      </div>
    );
  }

  return (
    <div className="minimal-pair-game-main-content" style={{
      position: 'relative',
      minHeight: '100vh',
      width: '100%',
      margin: 0,
      padding: isPhone ? '30px 15px' : '20px',
      overflow: 'visible'
    }}>
      {!isPhone && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          minHeight: '100vh',
          backgroundImage: `url(${background1})`,
          backgroundRepeat: 'no-repeat',
          backgroundSize: 'cover',
          backgroundPosition: 'center center',
          opacity: 0.2,
          zIndex: 0
        }} />
      )}
    <Container style={{ position: 'relative', zIndex: 1, maxWidth: '100%' }}>
      {renderLautSelectionModal()}
      <div style={{
        backgroundColor: 'white',
        padding: '20px',
        borderRadius: '10px',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
        margin: '0 auto 30px',
        width: 'fit-content'
      }}>
        <div className="text-center">
          <h2 style={{
            margin: 0,
            color: 'inherit'
          }}>
            Laut-Detektive
          </h2>
        </div>
      </div>
      
      {/* Control Panel */}
      <div style={{
        backgroundColor: 'white',
        padding: '10px 20px',
        borderRadius: '10px',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
        margin: '0 auto',
        width: 'fit-content'
      }}>
        <div className="d-flex justify-content-center gap-3 flex-wrap">
          {onBackToGames && (
            <OverlayTrigger
              placement="bottom"
              overlay={<Tooltip id="back-to-games-tooltip">Zurück zu Spielen</Tooltip>}
            >
              <Button
                onClick={onBackToGames}
                variant="secondary"
                size="lg"
                style={{
                  minWidth: '60px',
                  minHeight: '60px',
                  borderRadius: '50%',
                  boxShadow: '0 3px 8px rgba(0, 0, 0, 0.2)'
                }}
              >
                <LuGamepad2 size={24} />
              </Button>
            </OverlayTrigger>
          )}
          <OverlayTrigger
            placement="bottom"
            overlay={<Tooltip id="lauts-tooltip">Ausgewählte Laute</Tooltip>}
          >
            <div
              style={{
                minWidth: '80px',
                height: '60px',
                borderRadius: '30px',
                backgroundColor: '#f8f9fa',
                border: '2px solid #dee2e6',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '12px',
                fontWeight: 'bold',
                cursor: 'default',
                boxShadow: '0 3px 8px rgba(0, 0, 0, 0.2)',
                padding: '0 15px'
              }}
            >
              {selectedLauts.join(', ')}
            </div>
          </OverlayTrigger>
          {!gameCompleted && (
            <OverlayTrigger
              placement="bottom"
              overlay={<Tooltip id="change-sounds-tooltip">Laute ändern</Tooltip>}
            >
              <Button
                onClick={() => setShowLautModal(true)}
                variant="outline-secondary"
                size="lg"
                style={{
                  minWidth: '60px',
                  minHeight: '60px',
                  borderRadius: '50%',
                  boxShadow: '0 3px 8px rgba(0, 0, 0, 0.2)'
                }}
              >
                <Icon name="settings" size="lg" />
              </Button>
            </OverlayTrigger>
          )}
          <OverlayTrigger
            placement="bottom"
            overlay={<Tooltip id="help-tooltip">Hilfe</Tooltip>}
          >
            <Button
              onClick={showHelp}
              variant="info"
              size="lg"
              disabled={gameCompleted || hasGuessed}
              style={{
                minWidth: '60px',
                minHeight: '60px',
                borderRadius: '50%',
                boxShadow: '0 3px 8px rgba(0, 0, 0, 0.2)',
                padding: '10px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <img 
                src={helpLogo} 
                alt="Hilfe" 
                style={{ 
                  width: '40px', 
                  height: '40px',
                  objectFit: 'contain'
                }} 
              />
            </Button>
          </OverlayTrigger>
          <OverlayTrigger
            placement="bottom"
            overlay={<Tooltip id="new-game-tooltip">Diese Paare nochmal spielen</Tooltip>}
          >
            <Button
              onClick={resetGame}
              variant="success"
              size="lg"
              style={{
                minWidth: '60px',
                minHeight: '60px',
                borderRadius: '50%',
                boxShadow: '0 3px 8px rgba(0, 0, 0, 0.2)'
              }}
            >
              <Icon name="refresh" size="lg" />
            </Button>
          </OverlayTrigger>
          <OverlayTrigger
            placement="bottom"
            overlay={<Tooltip id="play-audio-tooltip">Audio abspielen</Tooltip>}
          >
            <Button
              onClick={speak}
              variant="warning"
              size="lg"
              style={{
                minWidth: '60px',
                minHeight: '60px',
                borderRadius: '50%',
                boxShadow: '0 3px 8px rgba(0, 0, 0, 0.2)'
              }}
            >
              <Icon name="volume-on" size="lg" />
            </Button>
          </OverlayTrigger>
        </div>
      </div>

      <div className="game-wrapper">
        {/* White box wrapper around game content */}
        <div style={{
          backgroundColor: 'white',
          padding: '30px',
          borderRadius: '15px',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
          margin: '0 auto',
          maxWidth: '1200px'
        }}>
          {/* Fixed Header Section */}
          <div className="header-section">
            {hasGuessed && userGuess !== null ? (
              <h3 style={{ 
                color: userGuess === targetIdx ? '#28a745' : '#dc3545', 
                margin: 0,
                fontWeight: 'bold'
              }}>
                {userGuess === targetIdx ? 'Richtig!' : 'Falsch'}
              </h3>
            ) : (
              <h3 style={{ margin: 0 }}>
                Welches Wort hörst du?
              </h3>
            )}
          </div>

          {/* Progress Bar */}
          <div className="progress-section">
            <ProgressBar 
              now={(currentPairIndex / wordPairs.length) * 100} 
              variant="success"
              animated={!gameCompleted}
              style={{ height: '25px' }}
              label={`${currentPairIndex} / ${wordPairs.length}`}
            />
          </div>

          {/* Main Game Area */}
          <div className="game-content">
          {/* Sprechhelden Held Flying Left Image - only show during gameplay and not in tablet portrait */}
          {!gameCompleted && !isTabletPortraitMode && (
            <div style={{
              position: 'absolute',
              left: '-190px',
              top: '-30%',
              transform: 'translateY(-50%)',
              zIndex: 5
            }}>
              <img
                src={sprechheldenHeldFlying}
                alt="Sprechhelden Held Flying"
                style={{
                  width: '220px',
                  height: 'auto',
                  opacity: 1
                }}
              />
            </div>
          )}
          
          {/* Game Content */}
          <div style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: '60px' }}>
            {/* Word Option 1 */}
            <Card 
              className={`game-card ${hasGuessed ? 'disabled' : ''} ${hasGuessed && userGuess === 0 ? (userGuess === targetIdx ? 'correct' : 'incorrect') : ''} ${glowingCard === 0 ? 'glowing' : ''}`}
              style={{ cursor: hasGuessed ? 'default' : 'pointer' }}
              onClick={() => !hasGuessed && handleGuess(0)}
            >
              <Card.Body className="d-flex justify-content-center align-items-center h-100" style={{ position: 'relative' }}>
                <div className="text-center">
                  {wordImages[currentPair[0]?.word] ? (
                    <img
                      src={wordImages[currentPair[0]?.word].imageUrl}
                      alt={currentPair[0]?.word}
                      style={{
                        maxWidth: '200px',
                        maxHeight: '200px',
                        objectFit: 'contain',
                        borderRadius: '10px'
                      }}
                    />
                  ) : (
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  )}
                </div>
                {hasGuessed && userGuess === 0 && (
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: userGuess === targetIdx ? 'rgba(40, 167, 69, 0.9)' : 'rgba(220, 53, 69, 0.9)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '20px',
                    zIndex: 10,
                    animation: 'fadeInBounce 0.6s ease-out'
                  }}>
                    <div style={{ 
                      fontSize: '4rem', 
                      color: 'white',
                      fontWeight: 'bold',
                      textShadow: '2px 2px 4px rgba(0,0,0,0.5)'
                    }}>
                      {userGuess === targetIdx ? '✓' : '✗'}
                    </div>
                  </div>
                )}
              </Card.Body>
            </Card>

            {/* Word Option 2 */}
            <Card 
              className={`game-card ${hasGuessed ? 'disabled' : ''} ${hasGuessed && userGuess === 1 ? (userGuess === targetIdx ? 'correct' : 'incorrect') : ''} ${glowingCard === 1 ? 'glowing' : ''}`}
              style={{ cursor: hasGuessed ? 'default' : 'pointer' }}
              onClick={() => !hasGuessed && handleGuess(1)}
            >
              <Card.Body className="d-flex justify-content-center align-items-center h-100" style={{ position: 'relative' }}>
                <div className="text-center">
                  {wordImages[currentPair[1]?.word] ? (
                    <img
                      src={wordImages[currentPair[1]?.word].imageUrl}
                      alt={currentPair[1]?.word}
                      style={{
                        maxWidth: '200px',
                        maxHeight: '200px',
                        objectFit: 'contain',
                        borderRadius: '10px'
                      }}
                    />
                  ) : (
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  )}
                </div>
                {hasGuessed && userGuess === 1 && (
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: userGuess === targetIdx ? 'rgba(40, 167, 69, 0.9)' : 'rgba(220, 53, 69, 0.9)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '20px',
                    zIndex: 10,
                    animation: 'fadeInBounce 0.6s ease-out'
                  }}>
                    <div style={{ 
                      fontSize: '4rem', 
                      color: 'white',
                      fontWeight: 'bold',
                      textShadow: '2px 2px 4px rgba(0,0,0,0.5)'
                    }}>
                      {userGuess === targetIdx ? '✓' : '✗'}
                    </div>
                  </div>
                )}
              </Card.Body>
            </Card>

          </div>

        </div>
        </div>
      </div>
    </Container>
    </div>
  );
}

export default MinimalPairGame;